import { useCallback, useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Clock, Pause, Play, Square, Plus, Settings } from 'lucide-react'
import { apiClient } from '@/lib/api.ts'
import { Button, Input, useToast } from '@/components/ui'
import { useAuthStore } from '@/stores/auth.ts'
import { TanssBookingButton, TanssAdminConfigPanel } from './TanssBooking.tsx'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface TimeEntry {
  id: string
  userId: string
  entryType: 'paperclip_issue' | 'custom' | 'manual'
  issueId: string | null
  issueTitle: string | null
  description: string | null
  date: string
  startTime: string
  endTime: string
  durationSeconds: number
  roundedDurationSeconds: number
  createdAt: string
}

export interface ActiveTracking {
  userId: string
  entryType: 'paperclip_issue' | 'custom'
  issueId: string | null
  issueTitle: string | null
  description: string | null
  startedAt: string
  status: 'active' | 'paused'
  lastActivityAt: string
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayStr(): string {
  return new Date().toISOString().slice(0, 10)
}

function yesterdayStr(): string {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d.toISOString().slice(0, 10)
}

function formatTime(dt: string): string {
  const d = new Date(dt.includes('T') ? dt : dt.replace(' ', 'T'))
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })
}

function formatDuration(seconds: number): string {
  if (seconds <= 0) return '—'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h > 0) return `${h}h ${m}min`
  return `${m}min`
}

function round5(value: string): string {
  const [h, m] = value.split(':').map(Number)
  const rounded = Math.round(((h! * 60) + m!) / 5) * 5
  const rh = Math.floor(rounded / 60) % 24
  const rm = rounded % 60
  return `${String(rh).padStart(2, '0')}:${String(rm).padStart(2, '0')}`
}

function nearestFive(minuteOffset = 0): string {
  const now = new Date()
  now.setMinutes(now.getMinutes() + minuteOffset)
  const m = Math.round(now.getMinutes() / 5) * 5
  now.setMinutes(m, 0, 0)
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes() % 60).padStart(2, '0')}`
}

// ─── API ──────────────────────────────────────────────────────────────────────

async function fetchEntries(date: string): Promise<TimeEntry[]> {
  const { data } = await apiClient.get<TimeEntry[]>('/paperclip/tracking/entries', { params: { date } })
  return Array.isArray(data) ? data : []
}

async function fetchActive(): Promise<{ active: ActiveTracking | null }> {
  const { data } = await apiClient.get<{ active: ActiveTracking | null }>('/paperclip/tracking/active')
  return data
}

async function apiStop(): Promise<{ closed: TimeEntry | null }> {
  const { data } = await apiClient.post<{ closed: TimeEntry | null }>('/paperclip/tracking/stop', {})
  return data
}

async function apiPause(): Promise<{ closed: TimeEntry | null; paused: ActiveTracking | null }> {
  const { data } = await apiClient.post<{ closed: TimeEntry | null; paused: ActiveTracking | null }>('/paperclip/tracking/pause', {})
  return data
}

async function apiResume(): Promise<{ active: ActiveTracking | null }> {
  const { data } = await apiClient.post<{ active: ActiveTracking | null }>('/paperclip/tracking/resume', {})
  return data
}

async function apiStartCustom(description: string): Promise<void> {
  await apiClient.post('/paperclip/tracking/start', {
    entryType: 'custom',
    description,
  })
}

async function apiManual(payload: {
  description: string
  date: string
  startTime: string
  endTime: string
}): Promise<TimeEntry> {
  const { data } = await apiClient.post<TimeEntry>('/paperclip/tracking/manual', payload)
  return data
}

async function apiHeartbeat(): Promise<void> {
  await apiClient.post('/paperclip/tracking/heartbeat', {})
}

// ─── TrackingTimeline ─────────────────────────────────────────────────────────

export interface TrackingTimelineProps {
  /** Called when an issue click from outside should start tracking. */
  onTrackingChange?: () => void
}

export function TrackingTimeline({ onTrackingChange }: TrackingTimelineProps) {
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const currentUser = useAuthStore(s => s.user)
  const isAdmin = currentUser?.role === 'admin'
  const [tab, setTab] = useState<'today' | 'yesterday'>('today')
  const [freeText, setFreeText] = useState('')
  const [elapsed, setElapsed] = useState(0)
  const [showSettings, setShowSettings] = useState(false)

  const [manualDesc, setManualDesc] = useState('')
  const [manualDate, setManualDate] = useState(todayStr)
  const [manualStart, setManualStart] = useState(() => nearestFive())
  const [manualEnd, setManualEnd] = useState(() => nearestFive(30))

  const heartbeatRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const elapsedRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const activeDate = tab === 'today' ? todayStr() : yesterdayStr()

  const { data: entriesData = [] } = useQuery({
    queryKey: ['tracking-entries', activeDate],
    queryFn: () => fetchEntries(activeDate),
    staleTime: 10_000,
  })

  const { data: activeData } = useQuery({
    queryKey: ['tracking-active'],
    queryFn: fetchActive,
    staleTime: 5_000,
    refetchInterval: 30_000,
  })

  const active = activeData?.active ?? null

  // Live elapsed timer
  useEffect(() => {
    if (elapsedRef.current) {
      clearInterval(elapsedRef.current)
      elapsedRef.current = null
    }
    if (active?.status === 'active') {
      const startMs = new Date(active.startedAt.includes('T') ? active.startedAt : active.startedAt.replace(' ', 'T')).getTime()
      const tick = () => setElapsed(Math.floor((Date.now() - startMs) / 1000))
      tick()
      elapsedRef.current = setInterval(tick, 1000)
    } else {
      setElapsed(0)
    }
    return () => {
      if (elapsedRef.current) clearInterval(elapsedRef.current)
    }
  }, [active?.startedAt, active?.status])

  // Heartbeat every 30s while active
  useEffect(() => {
    if (heartbeatRef.current) clearInterval(heartbeatRef.current)
    if (active?.status === 'active') {
      heartbeatRef.current = setInterval(() => { void apiHeartbeat() }, 30_000)
    }
    return () => {
      if (heartbeatRef.current) clearInterval(heartbeatRef.current)
    }
  }, [active?.status])

  const invalidate = useCallback(async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['tracking-entries'] }),
      queryClient.invalidateQueries({ queryKey: ['tracking-active'] }),
    ])
    onTrackingChange?.()
  }, [queryClient, onTrackingChange])

  const stopMutation = useMutation({
    mutationFn: apiStop,
    onSuccess: () => { void invalidate() },
    onError: () => toast({ title: 'Fehler', description: 'Tracking konnte nicht gestoppt werden.', tone: 'danger' }),
  })

  const pauseMutation = useMutation({
    mutationFn: apiPause,
    onSuccess: () => { void invalidate() },
    onError: () => toast({ title: 'Fehler', description: 'Tracking konnte nicht unterbrochen werden.', tone: 'danger' }),
  })

  const resumeMutation = useMutation({
    mutationFn: apiResume,
    onSuccess: () => { void invalidate() },
    onError: () => toast({ title: 'Fehler', description: 'Tracking konnte nicht fortgesetzt werden.', tone: 'danger' }),
  })

  const customMutation = useMutation({
    mutationFn: apiStartCustom,
    onSuccess: () => {
      setFreeText('')
      void invalidate()
    },
    onError: () => toast({ title: 'Fehler', description: 'Freies Tracking konnte nicht gestartet werden.', tone: 'danger' }),
  })

  const manualMutation = useMutation({
    mutationFn: apiManual,
    onSuccess: () => {
      setManualDesc('')
      setManualStart(nearestFive())
      setManualEnd(nearestFive(30))
      void invalidate()
      toast({ title: 'Eintrag gespeichert', tone: 'success' })
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message ?? 'Unbekannter Fehler'
      toast({ title: 'Fehler', description: msg, tone: 'danger' })
    },
  })

  function handleFreeTextSubmit(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key !== 'Enter') return
    const text = freeText.trim()
    if (!text) return
    customMutation.mutate(text)
  }

  function handleManualSubmit() {
    if (!manualDesc.trim()) return
    manualMutation.mutate({
      description: manualDesc.trim(),
      date: manualDate,
      startTime: manualStart,
      endTime: manualEnd,
    })
  }

  function handleTabChange(t: 'today' | 'yesterday') {
    setTab(t)
    setManualDate(t === 'today' ? todayStr() : yesterdayStr())
  }

  const label = active
    ? (active.issueTitle ?? active.description ?? active.issueId ?? 'Unbekannt')
    : null

  return (
    <div className="flex flex-col h-full min-w-[240px] max-w-[300px] border-l bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b bg-white dark:bg-gray-950">
        <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="text-sm font-semibold flex-1">Zeiterfassung</span>
        <button
          type="button"
          className={`p-1 rounded hover:bg-gray-100 ${showSettings ? 'text-brand-600' : 'text-muted-foreground'}`}
          onClick={() => setShowSettings(s => !s)}
          title="Einstellungen"
        >
          <Settings className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Active tracking indicator */}
      {active && (
        <div className={`px-3 py-2 border-b text-xs ${active.status === 'active' ? 'bg-green-50 border-green-200' : 'bg-yellow-50 border-yellow-200'}`}>
          <div className="flex items-start justify-between gap-1">
            <div className="flex-1 min-w-0">
              <div className={`font-semibold truncate ${active.status === 'active' ? 'text-green-800' : 'text-yellow-800'}`}>
                {active.status === 'active' ? '● Läuft' : '⏸ Pausiert'}
              </div>
              <div className="truncate text-muted-foreground">{label}</div>
              {active.status === 'active' && (
                <div className="font-mono text-green-700">{formatDuration(elapsed)}</div>
              )}
            </div>
            <div className="flex flex-col gap-1 shrink-0">
              {active.status === 'active' ? (
                <button
                  type="button"
                  onClick={() => pauseMutation.mutate()}
                  className="p-1 rounded hover:bg-yellow-100 text-yellow-700"
                  title="Unterbrechen"
                >
                  <Pause className="h-3.5 w-3.5" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => resumeMutation.mutate()}
                  className="p-1 rounded hover:bg-green-100 text-green-700"
                  title="Fortsetzen"
                >
                  <Play className="h-3.5 w-3.5" />
                </button>
              )}
              <button
                type="button"
                onClick={() => stopMutation.mutate()}
                className="p-1 rounded hover:bg-red-100 text-red-600"
                title="Tracking beenden"
              >
                <Square className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex border-b text-xs font-medium">
        {(['today', 'yesterday'] as const).map(t => (
          <button
            key={t}
            type="button"
            onClick={() => handleTabChange(t)}
            className={`flex-1 py-1.5 transition-colors ${
              tab === t
                ? 'border-b-2 border-brand-500 text-brand-600'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t === 'today' ? 'Heute' : 'Gestern'}
          </button>
        ))}
      </div>

      {/* Entries list */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col gap-0">
          {entriesData.length === 0 && (
            <p className="text-xs text-muted-foreground italic px-3 py-4 text-center">
              Noch keine Einträge.
            </p>
          )}
          {entriesData.map(entry => (
            <div
              key={entry.id}
              className="px-3 py-2 border-b last:border-b-0 hover:bg-white transition-colors"
            >
              <div className="flex items-start justify-between gap-1">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-medium truncate leading-snug">
                    {entry.issueId && (
                      <span className="font-mono text-muted-foreground mr-1">{entry.issueId}</span>
                    )}
                    {entry.issueTitle ?? entry.description ?? '—'}
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {formatTime(entry.startTime)} – {formatTime(entry.endTime)}
                  </p>
                </div>
                <span className="text-xs font-mono text-muted-foreground shrink-0">
                  {formatDuration(entry.roundedDurationSeconds)}
                </span>
              </div>
              <div className="mt-0.5">
                <span className={`inline-block text-xs px-1 rounded ${
                  entry.entryType === 'paperclip_issue'
                    ? 'bg-blue-100 text-blue-700'
                    : entry.entryType === 'manual'
                    ? 'bg-purple-100 text-purple-700'
                    : 'bg-gray-100 text-gray-600'
                }`}>
                  {entry.entryType === 'paperclip_issue' ? 'Issue' : entry.entryType === 'manual' ? 'Manuell' : 'Frei'}
                </span>
              </div>
            </div>
          ))}
        </div>

        {/* Total */}
        {entriesData.length > 0 && (() => {
          const total = entriesData.reduce((sum, e) => sum + e.roundedDurationSeconds, 0)
          return (
            <div className="px-3 py-2 border-t bg-white text-xs font-semibold flex justify-between">
              <span>Gesamt</span>
              <span>{formatDuration(total)}</span>
            </div>
          )
        })()}

        {/* Free tracking input */}
        <div className="px-3 py-2 border-t">
          <div className="flex gap-1 items-center">
            <Input
              value={freeText}
              onChange={e => setFreeText(e.target.value)}
              onKeyDown={handleFreeTextSubmit}
              placeholder="Freie Tätigkeit…"
              className="text-xs h-7 flex-1"
            />
            <button
              type="button"
              onClick={() => {
                if (freeText.trim()) customMutation.mutate(freeText.trim())
              }}
              className="p-1 rounded hover:bg-gray-100"
              title="Starten"
            >
              <Plus className="h-3.5 w-3.5 text-muted-foreground" />
            </button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Enter zum Starten</p>
        </div>

        {/* Manual entry */}
        <div className="px-3 py-2 border-t">
          <p className="text-xs font-semibold mb-2">Eigenes Tracking</p>
          <div className="flex flex-col gap-1.5">
            <Input
              value={manualDesc}
              onChange={e => setManualDesc(e.target.value)}
              placeholder="Beschreibung…"
              className="text-xs h-7"
            />
            <input
              type="date"
              value={manualDate}
              onChange={e => setManualDate(e.target.value)}
              className="w-full rounded border px-2 py-1 text-xs h-7 bg-white focus:outline-none focus:ring-1 focus:ring-brand-400"
            />
            <div className="flex gap-1">
              <input
                type="time"
                value={manualStart}
                step={300}
                onChange={e => setManualStart(round5(e.target.value))}
                className="flex-1 rounded border px-2 py-1 text-xs h-7 bg-white focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
              <span className="text-xs self-center text-muted-foreground">–</span>
              <input
                type="time"
                value={manualEnd}
                step={300}
                onChange={e => setManualEnd(round5(e.target.value))}
                className="flex-1 rounded border px-2 py-1 text-xs h-7 bg-white focus:outline-none focus:ring-1 focus:ring-brand-400"
              />
            </div>
            <Button
              variant="secondary"
              size="xs"
              onClick={handleManualSubmit}
              disabled={!manualDesc.trim() || manualMutation.isPending}
              className="self-start"
            >
              Eintragen
            </Button>
          </div>
        </div>

        {/* Settings panel (admin config) */}
        {showSettings && isAdmin && currentUser && (
          <div className="px-3 py-2 border-t">
            <TanssAdminConfigPanel currentUserId={currentUser.id} />
          </div>
        )}
      </div>

      {/* TANSS Booking Button */}
      <TanssBookingButton
        date={activeDate}
        entryCount={entriesData.length}
      />
    </div>
  )
}

// ─── Exposed tracking API for PaperclipMonitorPage ───────────────────────────

export async function trackIssue(issueId: string, issueTitle: string): Promise<void> {
  await apiClient.post('/paperclip/tracking/start', {
    entryType: 'paperclip_issue',
    issueId,
    issueTitle,
  })
}

export async function pauseActiveTracking(): Promise<void> {
  await apiClient.post('/paperclip/tracking/pause', {})
}

export async function stopActiveTracking(): Promise<void> {
  await apiClient.post('/paperclip/tracking/stop', {})
}

export async function getActiveTrackingState(): Promise<ActiveTracking | null> {
  const { data } = await apiClient.get<{ active: ActiveTracking | null }>('/paperclip/tracking/active')
  return data.active
}
